import React from 'react';

// Renders the tiny markdown subset the synthesis prompt allows the LLM to emit:
// **bold** highlights and "- " bullet lines. Nothing else is interpreted, and all
// output is React text nodes, so model/profile text can never inject markup.

export interface NoteSegment { text: string; bold: boolean }
export interface NoteBlock { type: 'text' | 'list'; segments: NoteSegment[]; items: NoteSegment[][] }

export const parseInline = (text: string): NoteSegment[] => {
    const markers = (text.match(/\*\*/g) || []).length;
    if (markers % 2 !== 0) {
        // unbalanced markers: show plain rather than mis-bolding the tail
        return [{ text: text.replace(/\*\*/g, ''), bold: false }];
    }
    return text
        .split('**')
        .map((part, i) => ({ text: part, bold: i % 2 === 1 }))
        .filter((seg) => seg.text !== '');
};

export const parseNote = (text: string): NoteBlock[] => {
    const blocks: NoteBlock[] = [];
    for (const rawLine of text.split('\n')) {
        const line = rawLine.trim();
        if (!line) { continue; }
        const bullet = line.match(/^[-•*]\s+(.*)$/);
        if (bullet) {
            const prev = blocks[blocks.length - 1];
            const target = prev?.type === 'list' ? prev : blocks[blocks.push({ type: 'list', segments: [], items: [] }) - 1];
            target.items.push(parseInline(bullet[1]));
        } else {
            blocks.push({ type: 'text', segments: parseInline(line), items: [] });
        }
    }
    return blocks;
};

const renderSegments = (segments: NoteSegment[]): React.ReactNode =>
    segments.map((seg, i) => (seg.bold ? <b key={i}>{seg.text}</b> : <React.Fragment key={i}>{seg.text}</React.Fragment>));

const NoteText = ({ text }: { text: string }) => (
    <>
        {parseNote(text).map((block, i) =>
            block.type === 'list' ? (
                <ul key={i} className="ai-notelist">
                    {block.items.map((item, j) => <li key={j}>{renderSegments(item)}</li>)}
                </ul>
            ) : (
                <p key={i}>{renderSegments(block.segments)}</p>
            )
        )}
    </>
);

export default NoteText;
